import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ACTIONABLE_ORDER_STATUSES,
  FEATURE_KEYS,
  MAX_DELIVERY_ADDRESS_LENGTH,
} from "@school-sales-ai/shared";
import { PageHeader } from "@/components/PageHeader";
import { OrderStatusBadge } from "@/components/orders/order-status";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useHasClientFeature } from "@/hooks/useClientFeatures";
import { useOrderBoardStages } from "@/hooks/useOrderBoardStages";
import {
  useCancelOrder,
  useDeliverOrder,
  useMoveOrder,
  useOrder,
  useUpdateDeliveryAddress,
} from "@/hooks/useOrders";
import { formatMxn } from "@/lib/format-mxn";
import { mapsLink } from "@/lib/maps-link";

/** The Select's value for plain "Por entregar" — Radix forbids an empty one. */
const NO_BOARD_STAGE = "none";

export function PedidoDetalle(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: order, isLoading } = useOrder(id ?? "");
  const deliver = useDeliverOrder();
  const cancel = useCancelOrder();
  const [reason, setReason] = useState("");
  const updateAddress = useUpdateDeliveryAddress();
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressDraft, setAddressDraft] = useState("");
  const { data: boardStages } = useOrderBoardStages();
  const moveOrder = useMoveOrder();
  // «Envío a domicilio». Without it the shop is pickup only: the address card is
  // shown only for an order that already carries one (history from before a
  // downgrade), read-only, and never asks for one.
  const delivers = useHasClientFeature(FEATURE_KEYS.delivery);

  if (isLoading || !order) {
    return (
      <div className="flex h-full flex-col">
        <PageHeader title="Pedido" onBack={() => navigate("/pedidos")} />
        <p className="px-6 py-8 text-sm text-ink-500">
          {isLoading ? "Cargando pedido…" : "Pedido no encontrado."}
        </p>
      </div>
    );
  }

  const busy = deliver.isPending || cancel.isPending;
  // `delivered` offers nothing: the goods are gone, and a return is a different
  // transaction with different data. Rendering a "cancelar" button here would
  // invite someone to "fix" a delivered order by cancelling it — which, once
  // inventory lands, credits back stock that is physically not on the shelf.
  const canAct = order.status === "confirmed";
  // Read from the SHARED list rather than repeating the rule, so the button and
  // the endpoint that would reject it cannot disagree. Wider than `canAct` on
  // purpose: an order still open has no status change to offer, but its address
  // is very much still editable.
  const canEditAddress = delivers && ACTIONABLE_ORDER_STATUSES.includes(order.status);
  const showAddress = delivers || order.deliveryAddress !== null;

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Pedido" onBack={() => navigate("/pedidos")} />

      <div className="flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-5">
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <OrderStatusBadge status={order.status} />
          <Link to={`/leads/${order.leadId}`} className="text-sm font-medium text-ink-900 underline">
            {order.leadName ?? "Sin nombre"}
          </Link>
          <span className="font-mono text-[11px] text-ink-500">{order.sessionRef}</span>
          {order.leadPhone && <span className="text-sm text-ink-600">{order.leadPhone}</span>}
        </div>

        {/* Where it goes. On its own line rather than inline with the phone
            because an address with references does not fit on a chip, and this is
            the one field the person driving actually needs.

            Editable while the order is still actionable: the address reaches us
            as an LLM's transcription of WhatsApp prose, so it will sometimes be
            wrong and nobody else can fix it. Frozen once terminal — a delivered
            order's address is the record of where the goods actually went. */}
        {showAddress && (
        <div className="mb-5 rounded-lg border border-ink-100 px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <p className="text-[11px] uppercase text-ink-500">Dirección de entrega</p>
            {canEditAddress && !editingAddress && (
              <button
                type="button"
                onClick={() => {
                  setAddressDraft(order.deliveryAddress ?? "");
                  setEditingAddress(true);
                }}
                className="text-[12px] font-medium text-moss underline"
              >
                {order.deliveryAddress ? "Editar" : "Agregar"}
              </button>
            )}
          </div>

          {editingAddress ? (
            <div className="mt-2 flex flex-col gap-2">
              <textarea
                value={addressDraft}
                onChange={(e) => setAddressDraft(e.target.value)}
                maxLength={MAX_DELIVERY_ADDRESS_LENGTH}
                rows={2}
                aria-label="Dirección de entrega"
                placeholder="Calle y número, colonia, y una referencia"
                className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm text-ink-900 placeholder:text-ink-300 focus:border-moss focus:outline-none"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={updateAddress.isPending}
                  onClick={() => {
                    updateAddress.mutate(
                      { id: order.id, deliveryAddress: addressDraft.trim() || null },
                      { onSuccess: () => setEditingAddress(false) },
                    );
                  }}
                  className="rounded-lg bg-ink-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
                >
                  Guardar
                </button>
                <button
                  type="button"
                  onClick={() => setEditingAddress(false)}
                  className="rounded-lg border border-ink-200 px-3 py-1.5 text-xs font-medium text-ink-700"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : order.deliveryAddress ? (
            <>
              <p className="mt-1 text-sm text-ink-900">{order.deliveryAddress}</p>
              {/* The zone it was verified in, and a map for the person driving:
                  the verified point when there is one, the text otherwise. */}
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
                {order.deliveryZoneName && (
                  <span className="text-ink-500">Zona {order.deliveryZoneName}</span>
                )}
                <OpenInMapsLink order={order} />
              </div>
            </>
          ) : (
            <p className="mt-1 text-sm text-ink-400">
              Sin dirección registrada — confírmala con el cliente antes de salir.
            </p>
          )}
        </div>
        )}

        <div className="overflow-x-auto rounded-lg border border-ink-100">
          <table className="w-full min-w-[32rem] text-sm">
            <thead>
              <tr className="border-b border-ink-100 bg-ink-050 text-left text-xs uppercase text-ink-500">
                <th className="px-4 py-2">Producto</th>
                <th className="px-4 py-2 text-right">Cantidad</th>
                <th className="px-4 py-2 text-right">Precio</th>
                <th className="px-4 py-2 text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {order.lines.map((l) => (
                <tr key={l.productId} className="border-b border-ink-050">
                  <td className="px-4 py-3 text-ink-900">{l.productName}</td>
                  <td className="px-4 py-3 text-right text-ink-600">{l.quantity}</td>
                  {/* The price the customer was quoted, snapshotted when the
                      line was added — never today's catalog price. When a
                      promotion set it, the list price is shown struck through:
                      an ANNOTATION, never an operand, so the column below still
                      adds up to the Total. */}
                  <td className="px-4 py-3 text-right text-ink-600">
                    {l.listUnitPriceCents !== null &&
                    l.listUnitPriceCents !== l.unitPriceCents ? (
                      <span className="mr-1.5 text-ink-400 line-through">
                        {formatMxn(l.listUnitPriceCents, order.currency)}
                      </span>
                    ) : null}
                    {formatMxn(l.unitPriceCents, order.currency)}
                  </td>
                  <td className="px-4 py-3 text-right text-ink-900">
                    {formatMxn(l.lineTotalCents, order.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              {/* Subtotal and delivery appear only when this order carries a fee.
                  Without them the lines above would not add up to the Total —
                  and `null` (no delivery charged) stays distinct from a `0` the
                  shop chose to show as free. */}
              {order.deliveryFeeCents !== null && (
                <>
                  <tr>
                    <td className="px-4 py-2 text-ink-600" colSpan={3}>
                      Subtotal
                    </td>
                    <td className="px-4 py-2 text-right text-ink-600">
                      {formatMxn(order.subtotalCents, order.currency)}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-ink-600" colSpan={3}>
                      Envío
                    </td>
                    <td className="px-4 py-2 text-right text-ink-600">
                      {formatMxn(order.deliveryFeeCents, order.currency)}
                    </td>
                  </tr>
                </>
              )}
              <tr>
                <td className="px-4 py-3 font-semibold text-ink-900" colSpan={3}>
                  Total
                </td>
                <td className="px-4 py-3 text-right font-semibold text-ink-900">
                  {formatMxn(order.totalCents, order.currency)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <p className="mt-3 text-[12.5px] text-ink-500">
          {order.confirmedAt
            ? order.confirmedByAgent
              ? "Confirmado por el agente."
              : "Confirmado por el equipo."
            : "Todavía no se confirma."}
          {order.closeReason ? ` Motivo: ${order.closeReason}` : ""}
        </p>

        {canAct && (
          <div className="mt-6 flex flex-col gap-3 rounded-lg border border-ink-100 p-4">
            {/* Where it sits on the board, for whoever works from this screen
                instead of dragging. Only when the shop defined steps at all. */}
            {boardStages && boardStages.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[13px] text-ink-600">Columna</span>
                <Select
                  value={order.boardStageId ?? NO_BOARD_STAGE}
                  disabled={moveOrder.isPending}
                  onValueChange={(v) =>
                    moveOrder.mutate({
                      order,
                      action: {
                        type: "board-stage",
                        boardStageId: v === NO_BOARD_STAGE ? null : v,
                      },
                    })
                  }
                >
                  <SelectTrigger className="w-[200px]" aria-label="Columna del tablero">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_BOARD_STAGE}>Por entregar</SelectItem>
                    {[...boardStages]
                      .sort((a, b) => a.position - b.position)
                      .map((st) => (
                        <SelectItem key={st.id} value={st.id}>
                          {st.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <p className="text-[13px] text-ink-600">
              El cobro se hace al entregar: márcalo como entregado cuando el cliente ya
              pagó y recibió.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={busy}
                onClick={() => deliver.mutate({ id: order.id })}
                className="rounded-lg bg-lime px-4 py-2 text-sm font-semibold text-moss hover:bg-lime-dk disabled:opacity-40"
              >
                Marcar entregado
              </button>
              <input
                className="min-w-[200px] flex-1 rounded-lg border border-ink-200 px-3 py-2 text-sm"
                placeholder="Motivo de cancelación (opcional)"
                value={reason}
                disabled={busy}
                aria-label="Motivo de cancelación"
                onChange={(e) => setReason(e.target.value)}
              />
              <button
                type="button"
                disabled={busy}
                onClick={() => cancel.mutate({ id: order.id, reason: reason || undefined })}
                className="rounded-lg border border-ink-200 px-4 py-2 text-sm font-medium text-red-700 hover:bg-ink-050 disabled:opacity-40"
              >
                Cancelar pedido
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function OpenInMapsLink({
  order,
}: {
  order: { deliveryLat: number | null; deliveryLng: number | null; deliveryAddress: string | null };
}): React.JSX.Element | null {
  const href = mapsLink(
    { lat: order.deliveryLat ?? null, lng: order.deliveryLng ?? null },
    order.deliveryAddress,
  );
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="font-medium text-moss underline">
      Abrir en Maps
    </a>
  );
}
